
import React from 'react';
import './DetailTransaction.scss';
import { TFunction } from 'react-i18next';
import { ITransaction } from '../../requests/ITransactionWebModelRequest';
import { UtilService } from '../../services/UtilService';
import { ReactComponent as IconCoupon } from 'itd-common/assets/images/svgs/coupon.svg';
import TarjetaLogo from 'itd-common/assets/images/issuers/PCT-logo.svg';
import Button from '../controls/buttons/buttonNew';

type Props = {
  transaction: ITransaction;
  showCoupon?: () => void;
  t: TFunction,
  isUserUYU: boolean
};

const currencySymbol = (code?: string) => {
  switch ((code || '').toUpperCase()) {
    case 'ARS': return '$';
    case 'USD': return 'US$';
    case 'EUR': return '€';
    default: return '$';
  }
};

const T_PREFIX: string = 'pages.reportTransactions.detailTransaction.';
const DetailTransaction: React.FC<Props> = ({ transaction, showCoupon, t, isUserUYU }) => {
  const showEmptyValue = () => {
    return <span className="ms-3">-</span>;
  }
  const formatCurrency = (value?: number | string, code?: string) => {
    if (value === undefined || value === null || value === '') return showEmptyValue();
    const num = typeof value === 'string' ? Number(value) : value;
    if (isNaN(num)) return String(value);
    return `${currencySymbol(code)} ${num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const getAcquirer = (acquirer: string) => {
    if (acquirer === "0000") {
      return showEmptyValue();
    } else {
      let value = t("acquirer" + "-" + acquirer).toUpperCase();
      let image = UtilService.getImageTag(value);

      if (value.indexOf("EDENRED") >= 0) {
        value = " " + value.replace("EDENRED", "").trim().toLowerCase();
      } else if (value.indexOf("CABAL") >= 0) {
        value = " " + value.replace("CABAL", "").trim().toLowerCase();
      } else if (value.indexOf("CREDITEL") >= 0) {
        value = " " + value.replace("CREDITEL", "").trim().toLowerCase();
      } else if (value.indexOf("PASS CARD") >= 0) {
        value = " " + value.replace("PASS CARD", "").trim().toLowerCase();
      } else {
        value = "";
      }
      return (
        <img src={image} width="50px" alt={`${acquirer}`} />
      );
    }
  }
  const getIssuer = (issuer: string, inputMode: string) => {
    const { value: displayValue, image: imageSrc } = UtilService.getIssuerGridValueInt(issuer, "issuer", t);
    if (inputMode === "QRFD") {
      return <img src={TarjetaLogo} alt={'PCT'} />;
    } else if (issuer === "VISA-N/A-ARV7VISA") {
      return <img src={imageSrc} alt={'VISA'} />;
    } else {
      return (
        issuer ?
          <div className="d-flex">
            <span className="pe-1 d-flex align-items-center">{displayValue}</span>
            {imageSrc && <img src={imageSrc} width="50px" alt={displayValue} />}
          </div>
          : "-"
      );
    }
  }
  const getTerminal = (terminal: string) => {
    if (terminal.toString().includes("B24_")) {
      return terminal ? t(terminal).substring(4) : "-"
    } else {
      return terminal ? t(terminal) : "-"
    }
  }

  const getResponseCode = (responseCode: string) => {
    let stateColor = isUserUYU ? "st-chip st-chip--" : "dt-chip dt-chip--";

    if (["00", "08", "11", "85", "LV", "OF"].includes(responseCode)) {
      stateColor += "success";
    } else if (["01"].includes(responseCode)) {
      stateColor += "warning";
    } else {
      stateColor += "danger";
    }

    return responseCode ? (
      <span className={stateColor}>{t("responseCode-" + responseCode)}</span>
    ) : (
      showEmptyValue()
    );
  }

  const getTaxRefund = (taxRefund: string) => {
    if ((taxRefund + "").startsWith("0#")) {
      return t("taxRefund-0");
    } else {
      const fields = ("" + taxRefund).split("#");
      if (fields.length > 0) {
        const key = fields.length > 1 && fields[1].trim().length > 0
          ? fields[1].trim()
          : fields[0].trim();
        const translationKey = `taxRefund-${key}`;

        if (translationKey.startsWith("taxRefund-")) {
          return "No aplica";
        }
        return t(translationKey);
      }
      return "No aplica";
    }
  }

  return (
    <div className="detail-transaction">
      <div className="dt-summary card">
        <ul className="dt-summary-list">
          <li className="dt-summary-item">
            <span className="dt-summary-label">{t(`${T_PREFIX}coupon`)}</span>
            <span className="dt-summary-value">
              {
                isUserUYU ?
                  <Button label={t(`${T_PREFIX}seeCoupon`)} color="dark" onClick={showCoupon} prefix={<IconCoupon />} />
                  :
                  <Button label={t(`${T_PREFIX}seeCoupon`)} color="transparent" onClick={showCoupon} sufix={<IconCoupon className="icon-black" />} />
              }
            </span>
          </li>

          <li className="dt-summary-item">
            <span className="dt-summary-label">{t(`${T_PREFIX}dateTime`)}</span>
            <span className="dt-summary-value">
              {transaction.authDateTime}
            </span>
          </li>

          <li className="dt-summary-item">
            <span className="dt-summary-label">{t(`${T_PREFIX}terminal`)}</span>
            <span className="dt-summary-value">{getTerminal(transaction.terminal)}</span>
          </li>

          <li className="dt-summary-item">
            <span className="dt-summary-label">{t(`${T_PREFIX}type`)}</span>
            <span className="dt-summary-value">{transaction.transactionType ? t("transactionType" + "-" + transaction.transactionType) : showEmptyValue()}</span>
          </li>

          <li className="dt-summary-item">
            <span className="dt-summary-label">{t(`${T_PREFIX}state`)}</span>
            <span className="dt-summary-value">{transaction.state ? t(`state-${transaction.state}`) : showEmptyValue()}</span>
          </li>

          <li className="dt-summary-item">
            <span className="dt-summary-label">{t(`${T_PREFIX}acquirer`)}</span>
            <span className="dt-summary-value">{getAcquirer(transaction.acquirer)}</span>
          </li>

          <li className="dt-summary-item">
            <span className="dt-summary-label">{t(`${T_PREFIX}productCard`)}</span>
            <span className="dt-summary-value">{getIssuer(transaction.issuer, transaction.inputMode)}</span>
          </li>

          <li className="dt-summary-item">
            <span className="dt-summary-label">{t(`${T_PREFIX}codResponse`)}</span>
            <span className="dt-summary-value">
              {getResponseCode(transaction.responseCode)}
            </span>
          </li>

          <li className="dt-summary-item dt-summary-item--total">
            <span className="dt-summary-label">{t(`${T_PREFIX}totalAmount`)}</span>
            <span className="dt-summary-value dt-summary-value--strong">
              {formatCurrency(transaction.totalAmountForReport, transaction.currency)}
            </span>
          </li>
        </ul>
      </div>

      {/* === Panel de detalles */}
      <div className="dt-details card">
        <dl className="dt-grid">
          {/* Columna 1 */}
          <div className="dt-col">
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}nroTransaction`)}</dt>
              <dd>{transaction.id || transaction.ticket || showEmptyValue()}</dd>
            </div>
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}currency`)}</dt>
              <dd>{transaction.currency ? t("currency" + "-" + transaction.currency) : showEmptyValue()}</dd>
            </div>
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}merchantCode`)}</dt>
              <dd>{transaction.merchant || showEmptyValue()}</dd>
            </div>
            {
              isUserUYU &&
              <>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}quota`)}</dt>
                  <dd>{transaction.quotas || showEmptyValue()}</dd>
                </div>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}taxRefund`)}</dt>
                  <dd>{getTaxRefund(transaction.taxRefund)}</dd>
                </div>
              </>
            }
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}card`)}</dt>
              <dd>{transaction.cardNumber}</dd>
            </div>
            {
              isUserUYU &&
              <>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}idEMV`)}</dt>
                  <dd>{transaction.emvApplicationId || showEmptyValue()}</dd>
                </div>

                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}originCard`)}</dt>
                  <dd>{transaction.originCardType || showEmptyValue()}</dd>
                </div>
              </>
            }
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}ticket`)}</dt>
              <dd>{transaction.ticket || 'No aplica'}</dd>
            </div>
            {
              isUserUYU &&
              <>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}amountBill`)}</dt>
                  <dd>{formatCurrency(transaction.billAmountForReport, transaction.currency)}</dd>
                </div>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}rut`)}</dt>
                  <dd>{transaction.clientDocument || showEmptyValue()}</dd>
                </div>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}address`)}</dt>
                  <dd>{transaction.address || showEmptyValue()}</dd>
                </div>
              </>
            }
            {
              !isUserUYU &&
              <div className="dt-row">
                <dt>{t(`${T_PREFIX}balanceRefunded`)}</dt>
                <dd>{transaction.clientDocument || showEmptyValue()}</dd>
              </div>
            }
          </div>

          {/* Columna 2 */}
          <div className="dt-col">
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}authorization`)}</dt>
              <dd>{transaction.authorizationCode || showEmptyValue()}</dd>
            </div>
            {
              isUserUYU ?
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}totalDto`)}</dt>
                  <dd>{formatCurrency(transaction.amountToCustomer, transaction.currency)}</dd>
                </div>
                :
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}saleAmount`)}</dt>
                  <dd>{formatCurrency(transaction.amountToCustomer, transaction.currency)}</dd>
                </div>
            }
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}terminalPrint`)}</dt>
              <dd>{getTerminal(transaction.terminal) || showEmptyValue()}</dd>
            </div>
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}inputMode`)}</dt>
              <dd>{transaction.inputMode ? t("inputMode" + "-" + transaction.inputMode) : showEmptyValue()}</dd>
            </div>
            {
              isUserUYU &&
              <>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}bill`)}</dt>
                  <dd>{transaction.billNumber || showEmptyValue()}</dd>
                </div>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}nameEVM`)}</dt>
                  <dd>{transaction.emvApplicationName || showEmptyValue()}</dd>
                </div>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}cardOwnerName`)}</dt>
                  <dd>{transaction.cardOwnerName || showEmptyValue()}</dd>
                </div>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}idUser`)}</dt>
                  <dd>{transaction.userId || showEmptyValue()}</dd>
                </div>
              </>
            }
            <div className="dt-row">
              <dt>{t(`${T_PREFIX}batch`)}</dt>
              <dd>{transaction.batch || showEmptyValue()}</dd>
            </div>
            {
              isUserUYU &&
              <>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}recordedAmount`)}</dt>
                  <dd>{formatCurrency(transaction.taxableAmountForReport, transaction.currency)}</dd>
                </div>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}address`)}</dt>
                  <dd>{transaction.location || showEmptyValue()}</dd>
                </div>
                <div className="dt-row">
                  <dt>{t(`${T_PREFIX}department`)}</dt>
                  <dd>{transaction.departament || showEmptyValue()}</dd>
                </div>
              </>
            }
            {
              !isUserUYU &&
              <div className="dt-row">
                <dt>{t(`${T_PREFIX}cuit`)}</dt>
                <dd>{transaction.clientDocument || showEmptyValue()}</dd>
              </div>
            }
          </div>

          {
            isUserUYU &&
            <div className="dt-row dt-row--full">
              <dt>{t(`${T_PREFIX}additionalInformation`)}</dt>
              <dd>{transaction.additionalData || showEmptyValue()}</dd>
            </div>
          }
        </dl>
      </div>
    </div>
  );
};
export default DetailTransaction;